import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  useGetAllGeofencesQuery,
  useDeleteGeofenceMutation,
  useUpdateGeofenceMutation,
  useGetPhoneBookByDepartmentMutation,
} from "features/geofenceSlice";
import { startGeofenceRedraw } from "features/geofenceUiSlice";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import GeofenceEditorDialog from "./GeofenceEditorDialog";

export default function GeofencePanel() {
  const { data: geofences, isLoading } = useGetAllGeofencesQuery();
  const [deleteGeofence] = useDeleteGeofenceMutation();
  const [updateGeofence, { isLoading: isSaving }] = useUpdateGeofenceMutation();
  const [getPhoneBookByDepartment] = useGetPhoneBookByDepartmentMutation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState(null);

  // Inline expand state
  const [expandedId, setExpandedId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Load the assignable users (phone book) and departments once for the panel
  useEffect(() => {
    let isMounted = true;
    const departments = AuthenticationService.getDepartmentList() || [];
    const seen = new Set();
    const uniqueDepartments = [];
    departments.forEach((dept) => {
      const deptName = dept?.department;
      if (deptName && !seen.has(deptName)) {
        seen.add(deptName);
        uniqueDepartments.push(dept);
      }
    });
    setDepartmentOptions(uniqueDepartments);

    const departmentNames = uniqueDepartments.map((d) => d.department).filter(Boolean);
    if (departmentNames.length) {
      getPhoneBookByDepartment({ searchRequest: {}, departmentList: departmentNames })
        .unwrap()
        .then((list) => {
          if (isMounted) setUserOptions(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (isMounted) setUserOptions([]);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [getPhoneBookByDepartment]);

  const handleEdit = (geofence) => {
    setEditingGeofence(geofence);
    setEditorOpen(true);
  };

  const handleDelete = async (geofence) => {
    try {
      await deleteGeofence({ intId: [geofence.id] }).unwrap();
      enqueueSnackbar("Geofence deleted", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Unable to delete geofence", { variant: "error" });
    }
  };

  const handleRedraw = (geofence) => {
    dispatch(
      startGeofenceRedraw({
        id: geofence.id,
        name: geofence.name,
        color: geofence.color,
        shapeType: geofence.shapeType,
        assignedUsers: geofence.assignedUsers || [],
        assignedDepartments: geofence.assignedDepartments || [],
      }),
    );
    enqueueSnackbar("Draw the new shape on the map to resize this geofence", {
      variant: "info",
    });
  };

  const toggleExpand = (geofence) => {
    if (expandedId === geofence.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(geofence.id);
    setSelectedUsers(geofence.assignedUsers || []);
    setSelectedDepartments(geofence.assignedDepartments || []);
  };

  const handleSaveAssignments = async (geofence) => {
    try {
      await updateGeofence({
        id: geofence.id,
        name: geofence.name,
        color: geofence.color,
        shapeType: geofence.shapeType,
        coordinates: geofence.coordinates,
        centerLat: geofence.centerLat,
        centerLng: geofence.centerLng,
        radiusMeters: geofence.radiusMeters,
        assignedUsers: selectedUsers,
        assignedDepartments: selectedDepartments,
      }).unwrap();
      enqueueSnackbar("Assignments updated", { variant: "success" });
      setExpandedId(null);
    } catch (error) {
      enqueueSnackbar("Unable to update assignments", { variant: "error" });
    }
  };

  const userLabel = (option) =>
    `${option.name || option.designation || option.offEXtn || ""}${
      option.offEXtn ? ` (${option.offEXtn})` : ""
    }`;
  const departmentLabel = (option) =>
    option.departmentDescription || option.department || "";
  const userOptionList = userOptions.filter((u) => u.offEXtn);

  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        borderRadius: 1.4,
        p: 0.6,
      })}
    >
      <Box sx={{ px: 0.6, pt: 0.4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
          }}
        >
          <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>
            My Geofences
          </MDTypography>
          <Chip
            label={`Total: ${geofences?.length || 0}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600, px: 0.5 }}
          />
        </Box>
        <MDTypography
          variant="caption"
          color="text"
          sx={{ opacity: 0.74, fontSize: "0.66rem", display: "block", mt: 0.3 }}
        >
          Tap a fence to assign users/departments. Use the resize icon to redraw
          its shape on the map.
        </MDTypography>
      </Box>

      <Box
        sx={(theme) => ({
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          borderRadius: 1.2,
          border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          p: 0.6,
          display: "flex",
          flexDirection: "column",
          gap: 0.6,
        })}
      >
        {isLoading && (
          <Box
            sx={{
              minHeight: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <CircularProgress size={22} color="info" />
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.74 }}>
              Loading geofences...
            </MDTypography>
          </Box>
        )}

        {!isLoading && (!geofences || geofences.length === 0) && (
          <Box
            sx={{
              minHeight: 140,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
              No geofences created yet.
            </MDTypography>
          </Box>
        )}

        {(geofences || []).map((geofence) => {
          const userCount = geofence.assignedUsers?.length || 0;
          const departmentCount = geofence.assignedDepartments?.length || 0;
          const fenceColor = geofence.color || "#3388ff";
          const isExpanded = expandedId === geofence.id;

          return (
            <Card
              key={geofence.id}
              sx={(theme) => ({
                p: 0.9,
                borderRadius: 1.2,
                border: `1px solid ${alpha(theme.palette.info.main, isExpanded ? 0.34 : 0.18)}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.1)}`,
                transition: "all 0.2s ease",
              })}
            >
              <Box
                onClick={() => toggleExpand(geofence)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: alpha(fenceColor, 0.15),
                      border: `1px solid ${alpha(fenceColor, 0.5)}`,
                    }}
                  >
                    <RoomOutlinedIcon sx={{ fontSize: 16, color: fenceColor }} />
                  </Box>

                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.35 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      noWrap
                      sx={{ fontSize: "0.72rem" }}
                    >
                      {geofence.name}
                    </MDTypography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <Chip
                        label={geofence.shapeType}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{
                          height: 17,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                      <Chip
                        icon={<PersonOutlineIcon sx={{ fontSize: "0.75rem !important" }} />}
                        label={userCount}
                        size="small"
                        variant="outlined"
                        color={userCount > 0 ? "success" : "default"}
                        sx={{
                          height: 17,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          "& .MuiChip-label": { px: 0.6 },
                        }}
                      />
                      <Chip
                        icon={<GroupsOutlinedIcon sx={{ fontSize: "0.75rem !important" }} />}
                        label={departmentCount}
                        size="small"
                        variant="outlined"
                        color={departmentCount > 0 ? "info" : "default"}
                        sx={{
                          height: 17,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          "& .MuiChip-label": { px: 0.6 },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, flexShrink: 0 }}>
                  <Tooltip title="Resize / redraw shape">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRedraw(geofence);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                        backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12),
                      }}
                    >
                      <EditLocationAltOutlinedIcon fontSize="small" color="warning" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit name & color">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(geofence);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.info.main, 0.38)}`,
                        backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" color="info" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(geofence);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.error.main, 0.38)}`,
                        backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
                      }}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={(theme) => ({
                    mt: 1,
                    pt: 1,
                    borderTop: `1px dashed ${alpha(theme.palette.info.main, 0.24)}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  })}
                >
                  <Autocomplete
                    multiple
                    size="small"
                    options={userOptionList}
                    getOptionLabel={userLabel}
                    isOptionEqualToValue={(option, value) => option.offEXtn === value.offEXtn}
                    value={userOptionList.filter((u) => selectedUsers.includes(u.offEXtn))}
                    onChange={(e, value) => setSelectedUsers(value.map((v) => v.offEXtn))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          size="small"
                          label={option.name || option.offEXtn}
                          {...getTagProps({ index })}
                          key={option.offEXtn}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Users" placeholder="Select users" />
                    )}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={departmentOptions}
                    getOptionLabel={departmentLabel}
                    isOptionEqualToValue={(option, value) =>
                      option.department === value.department
                    }
                    value={departmentOptions.filter((d) =>
                      selectedDepartments.includes(d.department),
                    )}
                    onChange={(e, value) =>
                      setSelectedDepartments(value.map((v) => v.department))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          size="small"
                          label={departmentLabel(option)}
                          {...getTagProps({ index })}
                          key={option.department}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Departments" placeholder="Select departments" />
                    )}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <MDButton
                      size="small"
                      color="secondary"
                      variant="text"
                      onClick={() => setExpandedId(null)}
                    >
                      Cancel
                    </MDButton>
                    <MDButton
                      size="small"
                      color="info"
                      onClick={() => handleSaveAssignments(geofence)}
                      disabled={isSaving}
                    >
                      Save
                    </MDButton>
                  </Box>
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Box>

      <GeofenceEditorDialog
        open={editorOpen}
        shape={null}
        geofence={editingGeofence}
        onClose={() => {
          setEditorOpen(false);
          setEditingGeofence(null);
        }}
        onSaved={() => {
          setEditorOpen(false);
          setEditingGeofence(null);
        }}
      />
    </Box>
  );
}
